import { useEffect } from 'react';
import { Button, Input } from '@chakra-ui/react';
import slugify from 'slugify';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import { toaster } from '@/components/ui/toaster';
import { Field } from '@/components/ui/field';
import { DailogProps, BlockType } from '@/lib/types';
import api from '@/utils/api';

interface Props extends DailogProps {
  workspaceId: string;
  contentTypeId: string;
  onSuccess(): void;
}

const formSchema = z.object({
  name: z.string().min(1, 'Section title is required'),
});

type FormData = z.infer<typeof formSchema>;

export default function CreateSectionTitleModal({
  isOpen,
  onClose,
  workspaceId,
  contentTypeId,
  onSuccess,
}: Props) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
  });

  useEffect(() => {
    if (isOpen) {
      reset();
    }
  }, [isOpen, reset]);

  const onSubmit = async (data: FormData) => {
    try {
      const res = await api.post(`fields`, {
        name: data.name,
        unique_name: slugify(data.name, {
          lower: true,
          strict: true,
        }),
        field_type: BlockType.SectionTitle,
        workspace_id: parseInt(workspaceId, 10),
        properties: {},
      });
      await api.post(`content-type-fields`, {
        content_type_id: parseInt(contentTypeId, 10),
        workspace_id: parseInt(workspaceId, 10),
        field_id: res.data.data.id,
      });
      onClose();
      onSuccess();
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to create section title',
      });
    }
  };

  return (
    <DialogRoot open={isOpen} onOpenChange={onClose}>
      <DialogBackdrop />
      <DialogTrigger />
      <DialogContent>
        <DialogCloseTrigger />
        <DialogHeader>
          <DialogTitle>Create section title</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <form
            id='section-title'
            onSubmit={(e) => {
              e.stopPropagation();
              handleSubmit(onSubmit)(e);
            }}
          >
            <Field label='Section Title' invalid={!!errors.name} errorText={errors.name?.message}>
              <Input {...register('name')} />
            </Field>
          </form>
        </DialogBody>
        <DialogFooter>
          <Button colorPalette='brand' variant={'ghost'} onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorPalette='brand'
            form={'section-title'}
            type={'submit'}
            loading={isSubmitting}
            loadingText='Loading...'
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
}
